import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { OntarioObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { BaseOntarioObstetricalFileSubSection, BaseOntarioObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/Subcomponent/BaseOntarioObstetricalFileSubSection";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";

class OntarioObstetricalFileMedicalHistory extends BaseOntarioObstetricalFileSubSection<BaseOntarioObstetricalFileSubSectionProps, never> {
    public render(): JSX.Element {
        return (
            <div className="section border border-top pregnancysummary">
                {this.renderHeaderSection()}
                <div className="row">
                    <div className="col-md-8">
                        <div className="row">
                            {this.renderFirstColumn()}
                            {this.renderSecondColumn()}
                        </div>
                        {this.renderComments()}
                    </div>
                    {this.renderThirdColumn()}
                </div>
            </div>
        );
    }

    private renderComments(): JSX.Element {
        const commentId = "OntarioMedicalHistory_Comment";

        const cssClassName = `ontariomedicalhistory-commentsticky${this.props.clickableTextId === commentId ? " clickable-text-offset-bottom" : ""}`;

        return (
            <div className={`row ${!this.props.readonly ? cssClassName : ""}`}>
                <div style={{ backgroundColor: "white" }} className="col-md-12">
                    <p className="text-bold">{this.getString("CommentsLabel")}</p>
                    {this.controlRenderer.renderTextarea({
                        value: this.props.form.Opr1.MedicalHistory?.Comments,
                        onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.Comments = value),
                        id: commentId,
                        clickableTextOptions: this.getClickableTextOptions(commentId, OntarioObstetricalFileClickableTextContext.Opr1_MedicalHistoryComment)
                    })}
                </div>
            </div>
        );
    }

    private renderHeaderSection(): JSX.Element {
        return (
            <div className="row">
                <div className="col-md-12">
                    <h4 className="text-bold">{this.getString("MedicalHistoryProvideDetailsLabel")}</h4>
                    {!this.props.readonly && (
                        <p>
                            <a onClick={() => this.handleMarkEmptyMedicalHistoryFieldsAsNo()}>
                                {this.getString("ObstetricalFile_MarkAllEmptyFieldsAsNo")}
                            </a>
                            {this.props.previousObstetricalFile && (
                                <a style={{ marginLeft: "50px" }} onClick={() => this.handleLoadMedicalHistoryFromPreviousObstetricalFile()}>
                                    {this.getString("ObstetricalFile_LoadInformationFromPreviousOpr1")}
                                </a>
                            )}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    private renderFirstColumn(): JSX.Element {
        const medicalHistory = this.props.form.Opr1.MedicalHistory;

        return (
            <div className="col-md-6">
                {this.renderSubTitle("ObstetricalFile_CurrentPregnancy")}
                {this.renderMedicalHistoryField(
                    `1. ${this.getString("BleedingLabel")}`,
                    medicalHistory?.CurrentPregnancy?.Bleeding,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.CurrentPregnancy.Bleeding = value),
                    "currentPregnancy_Bleeding"
                )}
                {this.renderMedicalHistoryField(
                    `2. ${this.getString("ObstetricalFile_NauseaVomiting")}`,
                    medicalHistory?.CurrentPregnancy?.NauseaVomiting,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.CurrentPregnancy.NauseaVomiting = value),
                    "currentPregnancy_NauseaVomiting"
                )}
                {this.renderMedicalHistoryField(
                    `3. ${this.getString("ObstetricalFile_RashFeverIllness")}`,
                    medicalHistory?.CurrentPregnancy?.RashFeverIllness,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.CurrentPregnancy.RashFeverIllness = value),
                    "currentPregnancy_RashFeverIllness"
                )}

                {this.renderSubTitle("ObstetricalFile_NutritionLabel")}
                {this.renderMedicalHistoryField(
                    `4. ${this.getString("ObstetricalFile_CalciumAdequate")}`,
                    medicalHistory?.Nutrition?.CalciumAdequate,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.Nutrition.CalciumAdequate = value),
                    "Nutrition_CalciumAdequate"
                )}
                {this.renderMedicalHistoryField(
                    `5. ${this.getString("ObstetricalFile_VitaminDAdequateLabel")}`,
                    medicalHistory?.Nutrition?.VitaminDAdequate,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.Nutrition.VitaminDAdequate = value),
                    "Nutrition_VitaminDAdequate"
                )}
                {this.renderMedicalHistoryField(
                    `6. ${this.getString("ObstetricalFile_FolicAcidPreconceptionLabel")}`,
                    medicalHistory?.Nutrition?.FolicAcidPreconception,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.Nutrition.FolicAcidPreconception = value),
                    "Nutrition_FolicAcidPreconception"
                )}
                {this.renderMedicalHistoryField(
                    `7. ${this.getString("ObstetricalFile_PrenatalVitaminLabel")}`,
                    medicalHistory?.Nutrition?.PrenatalVitamin,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.Nutrition.PrenatalVitamin = value),
                    "Nutrition_PrenatalVitamin"
                )}
                {this.renderMedicalHistoryField(
                    `8. ${this.getString("ObstetricalFile_FoodAccessOrQualityAdequateLabel")}`,
                    medicalHistory?.Nutrition?.FoodAccessOrQualityAdequate,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.Nutrition.FoodAccessOrQualityAdequate = value),
                    "Nutrition_FoodAccessOrQualityAdequate"
                )}
                {this.renderMedicalHistoryField(
                    `9. ${this.getString("ObstetricalFile_DietaryRestrictionsLabel")}`,
                    medicalHistory?.Nutrition?.DietaryRestrictions,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.Nutrition.DietaryRestrictions = value),
                    "Nutrition_DietaryRestrictions"
                )}

                {this.renderSubTitle("SurgicalHistoryLabel")}
                {this.renderMedicalHistoryField(
                    `10. ${this.getString("SurgeryLabel")}`,
                    medicalHistory?.HadSurgery,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HadSurgery = value),
                    "SurgicalSurgery_HadSurgery"
                )}
                {this.renderMedicalHistoryField(
                    `11. ${this.getString("ObstetricalFile_AnaestheticComplicationsLabel")}`,
                    medicalHistory?.HadAnaestheticComplications,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HadAnaestheticComplications = value),
                    "SurgicalSurgery_AnaestheticComplications"
                )}

                {this.renderSubTitle("AntecedentsMedicauxLabel")}
                {this.renderMedicalHistoryField(
                    `12. ${this.getString("HypertensionLabel")}`,
                    medicalHistory?.HistoricalConditions?.Hypertension,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.Hypertension = value),
                    "MedicalHistory_Hypertension"
                )}
                {this.renderMedicalHistoryField(
                    `13. ${this.getString("ObstetricalFile_CardiacPulmonaryLabel")}`,
                    medicalHistory?.HistoricalConditions?.CardiacPulmonary,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.CardiacPulmonary = value),
                    "MedicalHistory_CardiacPulmonary"
                )}
                {this.renderMedicalHistoryField(
                    `14. ${this.getString("EndocrineLabel")}`,
                    medicalHistory?.HistoricalConditions?.Endocrine,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.Endocrine = value),
                    "MedicalHistory_Endocrine"
                )}
                {this.renderMedicalHistoryField(
                    `15. ${this.getString("ObstetricalFile_GastrointestinalLiverLabel")}`,
                    medicalHistory?.HistoricalConditions?.GastrointestinalLiver,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.GastrointestinalLiver = value),
                    "MedicalHistory_GastrointestinalLiver"
                )}
                {this.renderMedicalHistoryField(
                    `16. ${this.getString("ObstetricalFile_BreastIncludingSurgeryLabel")}`,
                    medicalHistory?.HistoricalConditions?.BreastIncludingSurgery,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.BreastIncludingSurgery = value),
                    "MedicalHistory_BreastIncludingSurgery"
                )}
                {this.renderMedicalHistoryField(
                    `17. ${this.getString("ObstetricalFile_GynecologicalIncludingSurgeryLabel")}`,
                    medicalHistory?.HistoricalConditions?.GynecologicalIncludingSurgery,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.GynecologicalIncludingSurgery = value),
                    "MedicalHistory_GynecologicalIncludingSurgery"
                )}
                {this.renderMedicalHistoryField(
                    `18. ${this.getString("ObstetricalFile_UrinaryTractLabel")}`,
                    medicalHistory?.HistoricalConditions?.UrinaryTract,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.UrinaryTract = value),
                    "MedicalHistory_UrinaryTract"
                )}
                {this.renderMedicalHistoryField(
                    `19. ${this.getString("ObstetricalFile_MSKRheumatologyLabel")}`,
                    medicalHistory?.HistoricalConditions?.MusculoskeletalRheumatology,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.MusculoskeletalRheumatology = value),
                    "MedicalHistory_MusculoskeletalRheumatology"
                )}
                {this.renderMedicalHistoryField(
                    `20. ${this.getString("HematologicalLabel")}`,
                    medicalHistory?.HistoricalConditions?.Hematological,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.Hematological = value),
                    "MedicalHistory_Hematological"
                )}
                {this.renderMedicalHistoryField(
                    `21. ${this.getString("ObstetricalFile_ThromboembolicCoagLabel")}`,
                    medicalHistory?.HistoricalConditions?.ThromboembolicCoagulopathies,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.ThromboembolicCoagulopathies = value),
                    "MedicalHistory_ThromboembolicCoagulopathies"
                )}
                {this.renderMedicalHistoryField(
                    `22. ${this.getString("ObstetricalFile_BloodTransfusionLabel")}`,
                    medicalHistory?.HistoricalConditions?.BloodTransfusion,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.BloodTransfusion = value),
                    "MedicalHistory_BloodTransfusion"
                )}
                {this.renderMedicalHistoryField(
                    `23. ${this.getString("NeurologicalLabel")}`,
                    medicalHistory?.HistoricalConditions?.Neurological,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.Neurological = value),
                    "MedicalHistory_Neurological"
                )}
                {this.renderMedicalHistoryField(
                    `24. ${this.getString("OtherLabel")}`,
                    medicalHistory?.HistoricalConditions?.Other,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HistoricalConditions.Other = value),
                    "MedicalHistory_Other"
                )}
            </div>
        );
    }

    private renderSecondColumn(): JSX.Element {
        const medicalHistory = this.props.form.Opr1.MedicalHistory;

        return (
            <div className="col-md-6">
                <div>{this.renderSubTitle("FamilyHistoryLabel")}</div>
                {this.renderMedicalHistoryField(
                    `25. ${this.getString("ObstetricalFile_MedicalConditionsLabel")}`,
                    medicalHistory?.HasFamilyHistoryOfMedicalConditions,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.HasFamilyHistoryOfMedicalConditions = value),
                    "FamilyHistory_MedicalCondition",
                    this.getString("ObstetricalFile_MedicalConditionsDescriptionLabel")
                )}
                <div className="row row-form">
                    <div className="col-md-12">
                        <div>{this.renderSubTitle("ObstetricalFile_GeneticHistoryGametesLabel")}</div>
                        <div><p className="text-bold padding-sm-top">{`26. ${this.getString("ObstetricalFile_EthnicRacialBackgroundLabel")}`}:</p></div>
                        <div className="row row-form">
                            <div className="col-md-12 flex-display flex-space-between">
                                <div className="medicalhistory-inline-textbox width-60-percent">
                                    <div className="text-right medicalhistory egg-label"><span>{this.getString("EggLabel")}</span></div>
                                    {this.controlRenderer.renderTextbox({
                                        value: medicalHistory?.GeneticHistoryOfGametes?.EthnicRacialBackgroundOfEggDonor,
                                        onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.EthnicRacialBackgroundOfEggDonor = value),
                                        maxLength: 15,
                                        className: "line-height-normal"
                                    })}
                                </div>
                                <div className="medicalhistory-inline-textbox">
                                    <div className="text-right"><span>{this.getString("AgeLabel")}</span></div>
                                    <span className="medicalhistory egg-age-label">
                                        {this.controlRenderer.renderTextbox({
                                            value: medicalHistory?.GeneticHistoryOfGametes?.EggDonorAgeYears?.toString(),
                                            onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.EggDonorAgeYears = value ? parseInt(value) : null),
                                            maxLength: 2,
                                            isNumeric: true,
                                            className: "center",
                                            isInteger: true
                                        })}
                                    </span>
                                    <div>{this.getString("YearLabel")}</div>
                                </div>
                            </div>
                        </div>
                        <div className="row row-form">
                            <div className="col-md-12">
                                <div className="medicalhistory-inline-textbox width-60-percent">
                                    <div className="text-right medicalhistory sperm-label"><span>{this.getString("SpermLabel")}</span></div>
                                    {this.controlRenderer.renderTextbox({
                                        value: medicalHistory?.GeneticHistoryOfGametes?.EthicRacialBackgroundOfSpermDonor,
                                        onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.EthicRacialBackgroundOfSpermDonor = value),
                                        maxLength: 15,
                                        className: "line-height-normal"
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row row-form">
                    <div className="col-md-12">
                        {this.renderMedicalHistoryField(
                            `27. ${this.getString("ObstetricalFile_CarrierAtRiskLabel")}?`,
                            medicalHistory?.GeneticHistoryOfGametes?.IsCarrierAtRisk,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.IsCarrierAtRisk = value),
                            "GeneticHistoryOfGametes_IsCarrierAtRisk"
                        )}
                        <div className="row">
                            <div className="col-md-12">
                                <div className="padding-med-left">
                                    {this.renderMedicalHistoryField(
                                        "",
                                        medicalHistory?.GeneticHistoryOfGametes?.HemoglobinopathyScreening,
                                        value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.HemoglobinopathyScreening = value),
                                        "GeneticHistoryOfGametes_HemoglobinopathyScreening",
                                        `- ${this.getString("ObstetricalFile_HemoglobinopathyScreeningLabel")}`
                                    )}
                                    {this.renderMedicalHistoryField(
                                        "",
                                        medicalHistory?.GeneticHistoryOfGametes?.TaySachsDiseaseScreening,
                                        value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.TaySachsDiseaseScreening = value),
                                        "GeneticHistoryOfGametes_TaySachsDiseaseScreening",
                                        `- ${this.getString("ObstetricalFile_TaySachsDiseaseScreeningLabel")}`
                                    )}
                                    {this.renderMedicalHistoryField(
                                        "",
                                        medicalHistory?.GeneticHistoryOfGametes?.AshkenaziJewishScreeningPanel,
                                        value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.AshkenaziJewishScreeningPanel = value),
                                        "GeneticHistoryOfGametes_AshkenaziJewishScreeningPanel",
                                        `- ${this.getString("ObstetricalFile_AshkenaziJewishScreeningPanelLabel")}`
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
                <div className="row row-form">
                    <div className="col-md-12">
                        <p className="text-bold">{`28. ${this.getString("ObstetricalFile_GeneticFamilyHistoryLabel")}`}</p>
                        <div className="row">
                            <div className="col-md-12">
                                <div className="padding-med-left">
                                    {this.renderMedicalHistoryField(
                                        "",
                                        medicalHistory?.GeneticHistoryOfGametes?.FamilyGeneticConditions,
                                        value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.FamilyGeneticConditions = value),
                                        "GeneticHistoryOfGametes_FamilyGeneticConditions",
                                        `- ${this.getString("ObstetricalFile_FamilyGeneticConditionslLabel")}`
                                    )}
                                    {this.renderMedicalHistoryField(
                                        "",
                                        medicalHistory?.GeneticHistoryOfGametes?.OtherGeneticFamilyHistory,
                                        value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.OtherGeneticFamilyHistory = value),
                                        "GeneticHistoryOfGametes_OtherGeneticFamilyHistory",
                                        `- ${this.getString("ObstetricalFile_OtherGeneticFamilyHistoryLabel")}`
                                    )}
                                    {this.renderMedicalHistoryField(
                                        "",
                                        medicalHistory?.GeneticHistoryOfGametes?.Consanguinity,
                                        value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.GeneticHistoryOfGametes.Consanguinity = value),
                                        "GeneticHistoryOfGametes_Consanguinity",
                                        `- ${this.getString("ConsanguinityLabel")}`
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div>{this.renderSubTitle("ObstetricalFile_InfectiousDiseaseLabel")}</div>
                {this.renderMedicalHistoryField(
                    `29. ${this.getString("ObstetricalFile_VaricellaDiseaseLabel")}`,
                    medicalHistory?.InfectiousDisease?.VaricellaDisease,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.InfectiousDisease.VaricellaDisease = value),
                    "InfectiousDisease_VaricellaDisease"
                )}
                {this.renderMedicalHistoryField(
                    `30. ${this.getString("ObstetricalFile_VaricellaVaccineLabel")}`,
                    medicalHistory?.InfectiousDisease?.VaricellaVaccine,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.InfectiousDisease.VaricellaVaccine = value),
                    "InfectiousDisease_VaricellaVaccine"
                )}
                {this.renderMedicalHistoryField(
                    `31. ${this.getString("HumanImmunodeficiencyVirusAbbreviationLabel")}`,
                    medicalHistory?.InfectiousDisease?.HumanImmunodeficiencyVirus,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.InfectiousDisease.HumanImmunodeficiencyVirus = value),
                    "InfectiousDisease_HumanImmunodeficiencyVirus"
                )}
                <div className="row row-form">
                    <div className="col-md-12">
                        <div className="flex-display flex-align-items-center flex-wrap flex-space-between">
                            <p className="text-bold">{`32. ${this.getString("HerpesSimplexVirusAbbreviationLabel")}`}</p>
                            <span className="flex-display flex-end flex-align-items-center">
                                <p className="padding-xs-right">{this.getString("SelfLabel")}</p>
                                {this.controlRenderer.renderInlineYesNoRadio(
                                    medicalHistory?.InfectiousDisease?.HerpesSelf,
                                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.InfectiousDisease.HerpesSelf = value),
                                    "InfectiousDisease_HerpesSelf",
                                    "radio-btn-min-height"
                                )}
                            </span>
                            <span className="flex-display flex-align-items-center flex-end margin-sm-left">
                                <p className="padding-xs-right">{this.getString("PartnerLabel")}</p>
                                {this.controlRenderer.renderInlineYesNoRadio(
                                    medicalHistory?.InfectiousDisease?.HerpesPartner,
                                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.InfectiousDisease.HerpesPartner = value),
                                    "InfectiousDisease_HerpesPartner",
                                    "radio-btn-min-height"
                                )}
                            </span>
                        </div>
                    </div>
                </div>

                {this.renderMedicalHistoryField(
                    `33. ${this.getString("SexuallyTransmittedInfectionsLabel")}`,
                    medicalHistory?.InfectiousDisease?.SexuallyTransmittedInfection,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.InfectiousDisease.SexuallyTransmittedInfection = value),
                    "InfectiousDisease_SexuallyTransmittedInfection"
                )}
                {this.renderMedicalHistoryField(
                    `34. ${this.getString("ObstetricalFile_AtRiskPopulationLabel")}`,
                    medicalHistory?.InfectiousDisease?.AtRiskPopulation,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.InfectiousDisease.AtRiskPopulation = value),
                    "InfectiousDisease_AtRiskPopulation"
                )}
                {this.renderMedicalHistoryField(
                    `35. ${this.getString("OtherLabel")}`,
                    medicalHistory?.InfectiousDisease?.Other,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.InfectiousDisease.Other = value),
                    "InfectiousDisease_Other"
                )}
            </div>
        );
    }

    private renderThirdColumn(): JSX.Element {
        const medicalHistory = this.props.form.Opr1.MedicalHistory;

        return (
            <div className="col-md-4">
                {this.renderSubTitle("ObstetricalFile_MentalHealthSubstanceUseLabel")}
                <p className="text-bold padding-sm-top">{`36. ${this.getString("AnxietyLabel")}`}</p>
                <div className="row">
                    <div className="col-md-12">
                        <div className="margin-md-left">
                            {this.renderMedicalHistoryField(
                                "",
                                medicalHistory?.MentalHealthAndSubstanceUse?.AnxietyPast,
                                value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.AnxietyPast = value),
                                "MedicalHistoryMentalHealthAndSubstanceUse_AnxientyPast",
                                `- ${this.getString("PastLabel")}`
                            )}
                            {this.renderMedicalHistoryField(
                                "",
                                medicalHistory?.MentalHealthAndSubstanceUse?.AnxietyPresent,
                                value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.AnxietyPresent = value),
                                "MedicalHistoryMentalHealthAndSubstanceUse_AnxietyPresent",
                                `- ${this.getString("PresentLabel")}`
                            )}
                            <div className="row row-form">
                                <div className="col-md-12 flex-display flex-align-items-center flex-space-between">
                                    <div>{`- ${this.getString("ObstetricalFile_GAD2ScoreLabel")}`}</div>
                                    <div className="align-right" style={{ maxWidth: "63px" }}>
                                        {this.controlRenderer.renderTextbox({
                                            value: medicalHistory?.MentalHealthAndSubstanceUse?.GeneralizedAnxietyDisorder2Score?.toString() ?? "",
                                            onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.GeneralizedAnxietyDisorder2Score = value ? parseInt(value) : null),
                                            maxLength: 2,
                                            isNumeric: true,
                                            isInteger: true
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <p className="text-bold padding-sm-top">{`37. ${this.getString("DepressionLabel")}`}</p>
                <div className="row">
                    <div className="col-md-12">
                        <div className="margin-md-left">
                            {this.renderMedicalHistoryField(
                                "",
                                medicalHistory?.MentalHealthAndSubstanceUse?.DepressionPast,
                                value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.DepressionPast = value),
                                "MedicalHistoryMentalHealthAndSubstanceUse_DepressionPast",
                                `- ${this.getString("PastLabel")}`
                            )}
                            {this.renderMedicalHistoryField(
                                "",
                                medicalHistory?.MentalHealthAndSubstanceUse?.DepressionPresent,
                                value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.DepressionPresent = value),
                                "MedicalHistoryMentalHealthAndSubstanceUse_DepressionPresent",
                                `- ${this.getString("PresentLabel")}`
                            )}
                            <div className="row row-form">
                                <div className="col-md-12 flex-display flex-align-items-center flex-space-between">
                                    <div>{`- ${this.getString("ObstetricalFile_PHQ2ScoreLabel")}`}</div>
                                    <div className="align-right" style={{ maxWidth: "63px" }}>
                                        {this.controlRenderer.renderTextbox({
                                            value: medicalHistory?.MentalHealthAndSubstanceUse?.PatientHealthQuestionnaire2Score?.toString() ?? "",
                                            onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.PatientHealthQuestionnaire2Score = value ? parseInt(value) : null),
                                            maxLength: 2,
                                            isNumeric: true,
                                            isInteger: true
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {this.renderMedicalHistoryField(
                    `38. ${this.getString("ObstetricalFile_EatingDisorderLabel")}`,
                    medicalHistory?.MentalHealthAndSubstanceUse?.EatingDisorder,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.EatingDisorder = value),
                    "MedicalHistoryMentalHealthAndSubstanceUse_EatingDisorder"
                )}
                {this.renderMedicalHistoryField(
                    `39. ${this.getString("BipolarLabel")}`,
                    medicalHistory?.MentalHealthAndSubstanceUse?.Bipolar,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.Bipolar = value),
                    "MedicalHistoryMentalHealthAndSubstanceUse_Bipolar"
                )}
                {this.renderMedicalHistoryField(
                    `40. ${this.getString("SchizophreniaLabel")}`,
                    medicalHistory?.MentalHealthAndSubstanceUse?.Schizophrenia,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.Schizophrenia = value),
                    "MedicalHistoryMentalHealthAndSubstanceUse_Schizophrenia"
                )}
                {this.renderMedicalHistoryField(
                    `41. ${this.getString("OtherLabel")}`,
                    medicalHistory?.MentalHealthAndSubstanceUse?.Other,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.Other = value),
                    "MedicalHistoryMentalHealthAndSubstanceUse_Other",
                    "(e.g. PTSD, ADD, personality disorders)"
                )}
                {this.renderMedicalHistoryField(
                    `42. ${this.getString("ObstetricalFile_SmokedCigaretteWithinPast6MonthsLabel")}`,
                    medicalHistory?.MentalHealthAndSubstanceUse?.SmokedCigaretteWithinPast6Months,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.SmokedCigaretteWithinPast6Months = value),
                    "MedicalHistoryMentalHealthAndSubstanceUse_SmokedCigaretteWithinPast6Months"
                )}
                <div className="row row-form">
                    <div className="col-md-12 text-right flex-display flex-end flex-align-items-center">
                        <span>{this.getString("ObstetricalFile_CurrentSmokingLabel")}</span>
                        <span className="margin-sm-left align-right" style={{ maxWidth: "63px" }}>
                            {this.controlRenderer.renderTextbox({
                                value: medicalHistory?.MentalHealthAndSubstanceUse?.NumberOfCigarettesPerDay?.toString(),
                                onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.NumberOfCigarettesPerDay = value ? parseInt(value) : null),
                                maxLength: 4,
                                isNumeric: true,
                                isInteger: true
                            })}
                        </span>
                    </div>
                </div>
                {this.renderMedicalHistoryField(
                    `43. ${this.getString("ObstetricalFile_EverDrinkAlcoholLabel")}`,
                    medicalHistory?.MentalHealthAndSubstanceUse?.HasEverDrunkAlcohol,
                    value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.HasEverDrunkAlcohol = value),
                    "MedicalHistoryMentalHealthAndSubstanceUse_HasEverDrunkAlcohol"
                )}
                <div className="row row-form">
                    <div className="col-md-12 medicalhistory">
                        <div className="margin-md-left"><span>{`- ${this.getString("ObstetricalFile_IfYesLabel")}:`}</span></div>
                        <div className="margin-sm-bottom flex-display flex-end flex-align-items-center">
                            <span>{this.getString("ObstetricalFile_LastDrinkLabel")}</span>
                            <span className="margin-sm-left align-right medicalhistory">
                                {this.controlRenderer.renderDateWithPrecision({
                                    date: medicalHistory?.MentalHealthAndSubstanceUse?.LastDrinkDate,
                                    precision: medicalHistory?.MentalHealthAndSubstanceUse?.LastDrinkDatePrecision,
                                    onChange: dateWithPrecision => this.handleLastDrinkDate(dateWithPrecision),
                                    id: "MedicalHistoryMentalHealthAndSubstanceUse_LastDrinkDate",
                                    width: !this.props.readonly ? 110 : null,
                                    className: "lastdrink-date-selector"
                                })}
                            </span>
                        </div>
                        <div className="margin-sm-bottom flex-display flex-end flex-align-items-center">
                            <span>{this.getString("ObstetricalFile_CurrentDrinkingLabel")}</span>
                            <span className="margin-sm-left align-right" style={{ maxWidth: "63px" }}>
                                {this.controlRenderer.renderTextbox({
                                    value: medicalHistory?.MentalHealthAndSubstanceUse?.CurrentDrinksPerWeek?.toString(),
                                    onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.CurrentDrinksPerWeek = value ? parseInt(value) : null),
                                    maxLength: 4,
                                    isNumeric: true,
                                    isInteger: true
                                })}
                            </span>
                        </div>
                        <div className="margin-sm-bottom flex-display flex-end flex-align-items-center">
                            <span>{this.getString("ObstetricalFile_TACEScoreLabel")}</span>
                            <span className="margin-sm-left align-right" style={{ width: !this.props.readonly ? "63px" : "auto" }}>
                                {this.controlRenderer.renderTextbox({
                                    value: medicalHistory?.MentalHealthAndSubstanceUse?.TaceScreeningScore?.toString(),
                                    onChange: value => this.props.mutateModelState(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.TaceScreeningScore = value ? parseInt(value) : null),
                                    maxLength: 3,
                                    isNumeric: true,
                                    isInteger: true
                                })}
                            </span>
                        </div>
                    </div>
                </div>
                {this.renderMedicalHistoryField(
                    `44. ${this.getString("MarijuanaLabel")}`,
                    medicalHistory?.MentalHealthAndSubstanceUse?.Marijuana,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.Marijuana = value),
                    "MedicalHistoryMentalHealthAndSubstanceUse_Marijuana"
                )}
                {this.renderMedicalHistoryField(
                    `45. ${this.getString("ObstetricalFile_NonPrescribedSubstancesAndDrugsLabel")}`,
                    medicalHistory?.MentalHealthAndSubstanceUse?.NonPrescribedSubstancesAndDrugs,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.NonPrescribedSubstancesAndDrugs = value),
                    "MedicalHistoryMentalHealthAndSubstanceUse_NonPrescribedSubstancesAndDrugs"
                )}

                {this.renderSubTitle("ObstetricalFile_LifeStyleSocialLabel")}
                {this.renderMedicalHistoryField(
                    `46. ${this.getString("ObstetricalFile_OccupationalRisksLabel")}`,
                    medicalHistory?.LifestyleAndSocial?.OccupationalRisks,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.LifestyleAndSocial.OccupationalRisks = value),
                    "LifestyleAndSocial_OccupationalRisks"
                )}
                {this.renderMedicalHistoryField(
                    `47. ${this.getString("ObstetricalFile_FinancialOrHousingIssuesLabel")}`,
                    medicalHistory?.LifestyleAndSocial?.FinancialOrHousingIssues,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.LifestyleAndSocial.FinancialOrHousingIssues = value),
                    "LifestyleAndSocial_FinancialOrHousingIssues"
                )}
                {this.renderMedicalHistoryField(
                    `48. ${this.getString("ObstetricalFile_PoorSocialSupportLabel")}`,
                    medicalHistory?.LifestyleAndSocial?.PoorSocialSupport,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.LifestyleAndSocial.PoorSocialSupport = value),
                    "LifestyleAndSocial_PoorSocialSupport"
                )}
                {this.renderMedicalHistoryField(
                    `49. ${this.getString("ObstetricalFile_BeliefsOrPracticesAffectingCareLabel")}`,
                    medicalHistory?.LifestyleAndSocial?.BeliefsOrPracticesAffectingCare,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.LifestyleAndSocial.BeliefsOrPracticesAffectingCare = value),
                    "LifestyleAndSocial_BeliefsOrPracticesAffectingCare"
                )}
                {this.renderMedicalHistoryField(
                    `50. ${this.getString("ObstetricalFile_RelationshipProblemsLabel")}`,
                    medicalHistory?.LifestyleAndSocial?.RelationshipProblems,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.LifestyleAndSocial.RelationshipProblems = value),
                    "LifestyleAndSocial_RelationshipProblems"
                )}
                {this.renderMedicalHistoryField(
                    `51. ${this.getString("ObstetricalFile_IntimatePartnerOrFamilyViolenceLabel")}`,
                    medicalHistory?.LifestyleAndSocial?.IntimatePartnerOrFamilyViolence,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.LifestyleAndSocial.IntimatePartnerOrFamilyViolence = value),
                    "LifestyleAndSocial_IntimatePartnerOrFamilyViolence"
                )}
                {this.renderMedicalHistoryField(
                    `52. ${this.getString("ObstetricalFile_ParentingConcernsLabel")}`,
                    medicalHistory?.LifestyleAndSocial?.ParentingConcerns,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.LifestyleAndSocial.ParentingConcerns = value),
                    "LifestyleAndSocial_ParentingConcerns",
                    "(e.g. developmental disability, family trauma)"
                )}
                {this.renderMedicalHistoryField(
                    `53. ${this.getString("OtherLabel")}`,
                    medicalHistory?.LifestyleAndSocial?.Other,
                    value => this.props.mutateModelStateWithFormUpdate(s => s.Opr1.MedicalHistory.LifestyleAndSocial.Other = value),
                    "LifestyleAndSocial_Other"
                )}
            </div>
        );
    }

    private renderMedicalHistoryField(label: string, value: boolean | null, onChange: (value: boolean) => void, id: string, extraLabel?: string, extraLabelClassName: string = ""): JSX.Element {
        return (
            <div className="row row-form">
                <div className="col-md-12" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                        {label && <p className="text-bold">{label}</p>}
                        {extraLabel && <div className={extraLabelClassName}><p>{extraLabel}</p></div>}
                    </div>

                    {this.controlRenderer.renderInlineYesNoRadio(
                        value,
                        value => onChange?.(value),
                        id,
                        "radio-btn-min-height margin-xs-left"
                    )}
                </div>
            </div>
        );
    }

    private handleMarkEmptyMedicalHistoryFieldsAsNo(): void {
        this.props.mutateModelState(
            s => s.Opr1.MedicalHistory.setEmptyFieldsAsNo(),
            () => this.props.handleFormUpdate()
        );
    }

    private handleLoadMedicalHistoryFromPreviousObstetricalFile(): void {
        if (!this.props.previousObstetricalFile)
            return;

        this.props.mutateModelState(
            s => s.Opr1.MedicalHistory = this.props.previousObstetricalFile?.getFormObject<OntarioObstetricalFileForm>().Opr1.MedicalHistory,
            () => this.props.handleFormUpdate()
        );
    }

    private handleLastDrinkDate(dateWithPrecision: DateTimeWithPrecision): void {
        this.props.mutateModelStateWithFormUpdate(s => {
            if (dateWithPrecision.value) {
                s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.LastDrinkDate = LuxonDateTimeParser.parse(dateWithPrecision.value).toJSDate();
                s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.LastDrinkDatePrecision = dateWithPrecision.precision;
            }
            else if (IsNullOrWhiteSpace(dateWithPrecision.text)) {
                s.Opr1.MedicalHistory.MentalHealthAndSubstanceUse.LastDrinkDate = null;
            }
        });
    }
}

export { OntarioObstetricalFileMedicalHistory };
